/**
 * ModelODetailView — comprehensive per-room detail for Model O-1 and O-2 units.
 *
 * Renders:
 *   (O-2 only) Circuit Arrangement
 *   (O-2 only) Op Coil Performance (primary air coil)
 *   ACB Coil Performance (secondary/induced air coil) — both O-1 and O-2
 *   System Totals
 *   Psychrometric Chart
 *   Psychrometric Points Table
 *   Water-Side Summary
 *
 * All data sourced directly from the backend response — no physics re-computed.
 * The backend's per-speed row (from calculate_o1 / calculate_o2) carries:
 *   op_total_btuh, op_sensible_btuh, op_latent_btuh
 *   op_ewt_f, op_lwt_f, op_water_delta_t_f, op_water_flow_gpm, op_water_pd_fth2o
 *   acb_ewt_f, acb_total_btuh, acb_sensible_btuh, acb_latent_btuh
 *   acb_lwt_f, acb_water_delta_t_f, acb_water_flow_gpm, acb_water_pd_fth2o
 *   primary_cfm, secondary_cfm, supply_cfm
 *   air_side.{supply_db_f, supply_w_grains, supply_rh_pct, coil_leaving_db_f,
 *             coil_leaving_w_grains, room_db_f, room_rh_pct, room_w_grains,
 *             air_total_btuh, air_sensible_btuh, air_latent_btuh, total_supply_cfm,
 *             op_leaving_db_f, op_leaving_w_grains (O-2 only),
 *             acb_leaving_db_f, acb_leaving_w_grains (O-2 only)}
 * Top-level:
 *   conditions.{coil_entering_air_f, room_dbt_f, water_entering_f, water_flow_gpm}
 *   piping (O-2 only: "serial" | "parallel")
 */

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { fmtKind, unitLabel } from "../../lib/units";
import { PsychrometricChart } from "../PsychrometricChart";
import { pickCascadeOperatingPoint, formatSizingFootnote } from "../../lib/wizardCalc";

// ---------------------------------------------------------------------------
// Internal layout helpers — match ModelNDetailView styling exactly
// ---------------------------------------------------------------------------

function Section({ title, children, className = "" }) {
  return (
    <div
      data-pdf-section
      className={`border border-border rounded overflow-hidden mb-2 break-inside-avoid ${className}`}
    >
      <div className="bg-muted px-3 py-1.5 text-sm font-semibold border-b border-border text-foreground">
        {title}
      </div>
      <div className="p-2 text-xs">{children}</div>
    </div>
  );
}

function DataTile({ label, value, sub, accent }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-semibold tabular-nums ${accent ?? ""}`}>
        {value ?? "—"}
      </div>
      {sub && (
        <div className="text-[10px] text-muted-foreground">{sub}</div>
      )}
    </div>
  );
}

function fmtTemp(val, isSI) {
  if (val == null || val === "" || isNaN(Number(val))) return "—";
  return `${fmtKind(val, "temp", isSI)} ${unitLabel("temp", isSI)}`;
}

function fmtGrains(grains, isSI) {
  if (grains == null || isNaN(Number(grains))) return "—";
  if (isSI) return `${(Number(grains) * 1000 / 7000).toFixed(1)} g/kg`;
  return `${Number(grains).toFixed(1)} gr/lb`;
}

function fmtRH(rh) {
  if (rh == null || isNaN(Number(rh))) return "—";
  return `${Number(rh).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Wet/Dry badge
// ---------------------------------------------------------------------------

function WetDryBadge({ isDry }) {
  if (isDry == null) return null;
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wide leading-none px-2 pt-0.5 pb-1.5 rounded ${
        isDry
          ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300"
          : "bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300"
      }`}
    >
      {isDry ? "Dry (sensible only)" : "Wet (condensing)"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ACB strategy labels — humanised form for display
// ---------------------------------------------------------------------------
const ACB_STRATEGY_LABELS = {
  given_flow_given_sp: "Given PA flow, given SP",
  given_flow_lowest_sp: "Given PA flow, lowest SP",
  lowest_flow_given_sp: "Lowest PA flow, given SP",
  lowest_flow_lowest_sp: "Lowest PA flow and SP",
};

function humaniseStrategy(key) {
  return ACB_STRATEGY_LABELS[key] || key || "—";
}

// ---------------------------------------------------------------------------
// Section: Engine Selection Summary
// Shown at the top of the detail view (above CircuitArrangement) when
// optimal_point is present. Renders the cascade operating point choices and
// ACB-specific strategy details.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Water cap source → human display string
// ---------------------------------------------------------------------------
export function formatWaterCapDisplay(optimalPoint) {
  const {
    active_water_cap_source: src,
    active_water_cap_value_gpm: capGpm,
    target_cooling_btuh: targetBtuh,
  } = optimalPoint || {};

  if (!src) return null;

  const gpmStr = capGpm != null ? `${Number(capGpm).toFixed(2)} GPM` : null;

  if (src === "hydraulic") {
    return gpmStr ? `Hydraulic ceiling: ${gpmStr}` : "Hydraulic ceiling";
  }
  if (src === "max_gpm") {
    return gpmStr ? `Max GPM: ${gpmStr}` : "Max GPM cap";
  }
  if (src === "gpm_per_ton") {
    const tons = targetBtuh != null ? Number(targetBtuh) / 12000 : null;
    if (capGpm != null && tons != null) {
      const rateGpmPerTon = tons > 0 ? Number(capGpm) / tons : null;
      const rateStr = rateGpmPerTon != null ? `${rateGpmPerTon.toFixed(2)} GPM/Ton` : "GPM/Ton";
      return `${rateStr} × ${tons.toFixed(2)} Ton = ${gpmStr}`;
    }
    return gpmStr ? `GPM/Ton cap: ${gpmStr}` : "GPM/Ton cap";
  }
  if (src === "max_dp") {
    return gpmStr ? `Max DP cap → ${gpmStr}` : "Max DP cap";
  }
  if (src === "user_water_flow") {
    return gpmStr ? `User water flow: ${gpmStr}` : "User water flow";
  }
  return src;
}

export function EngineSelectionSummary({ optimalPoint, isFACBFamily = true, isSI, noCoilPipes, selectedCircuits }) {
  if (!optimalPoint) return null;

  const {
    operating_speed,
    target_btuh_basis,
    water_flow_gpm,
    acb_strategy_used,
    actual_pa_cfm,
    nozzle_sp_inwg,
    nozzle_label,
    strategy_minimised,
    strategy_note,
    active_water_cap_source,
    shortfall_btuh,
    split_acb_fraction,
    target_cooling_btuh,
  } = optimalPoint;

  const basisLabel = target_btuh_basis === "room_load"
    ? "Room-load (Qᵣ, AHRI 1335)"
    : "Water-side (Qᴄ)";

  // Minimisation chips: show when strategy_minimised contains reduced values.
  const miniChips = [];
  if (strategy_minimised) {
    if (strategy_minimised.pa_cfm_from != null && strategy_minimised.pa_cfm_to != null) {
      miniChips.push(
        `PA: ${fmtKind(strategy_minimised.pa_cfm_from, "flow", isSI)} → ${fmtKind(strategy_minimised.pa_cfm_to, "flow", isSI)} ${unitLabel("flow", isSI)}`
      );
    }
    if (strategy_minimised.sp_from != null && strategy_minimised.sp_to != null) {
      miniChips.push(
        `SP: ${fmtKind(strategy_minimised.sp_from, "staticPressure", isSI)} → ${fmtKind(strategy_minimised.sp_to, "staticPressure", isSI)} ${unitLabel("staticPressure", isSI)}`
      );
    }
  }

  // Water cap display string (null when source missing — legacy backend)
  const waterCapDisplay = formatWaterCapDisplay(optimalPoint);

  // Shortfall check — positive shortfall_btuh means delivered < target × 0.95
  const hasShortfall = typeof shortfall_btuh === "number" && shortfall_btuh > 0;
  const deliveredBtuh = hasShortfall && target_cooling_btuh != null
    ? Math.round(Number(target_cooling_btuh) - shortfall_btuh)
    : null;

  // O-2 split fraction — present when backend computed acb/op split
  const hasSplit = typeof split_acb_fraction === "number";
  const acbPct = hasSplit ? Math.round(split_acb_fraction * 100 / 5) * 5 : null;
  const opPct = acbPct != null ? 100 - acbPct : null;

  return (
    <Section title="Engine Selection Summary" className="mb-2" data-testid="engine-selection-summary">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {operating_speed && (
          <DataTile
            label="Operating Speed"
            value={
              <span className="capitalize">
                {operating_speed} (Optimized)
              </span>
            }
          />
        )}
        {target_btuh_basis && (
          <DataTile label="Selection Basis" value={basisLabel} />
        )}
        {noCoilPipes != null && (
          <DataTile label="Coil Pipes" value={`${noCoilPipes}-pipe`} />
        )}
        {selectedCircuits != null && (
          <DataTile label="Coil Circuits" value={`${selectedCircuits}-ckt`} />
        )}
        {water_flow_gpm != null && (
          <DataTile
            label="Water Flow (modulated)"
            value={`${fmtKind(water_flow_gpm, "waterFlow", isSI)} ${unitLabel("waterFlow", isSI)}`}
          />
        )}
        {acb_strategy_used && (
          <DataTile label="ACB Strategy" value={humaniseStrategy(acb_strategy_used)} />
        )}
        {actual_pa_cfm != null && (
          <DataTile
            label="PA Flow (actual)"
            value={`${fmtKind(actual_pa_cfm, "flow", isSI)} ${unitLabel("flow", isSI)}`}
          />
        )}
        {nozzle_sp_inwg != null && (
          <DataTile
            label="Nozzle SP"
            value={`${fmtKind(nozzle_sp_inwg, "staticPressure", isSI)} ${unitLabel("staticPressure", isSI)}`}
          />
        )}
        {nozzle_label && (
          <DataTile label="Nozzle Assembly" value={nozzle_label} />
        )}
        {/* B.6 — Water Flow Cap binding (hidden when source absent — legacy response) */}
        {waterCapDisplay && (
          <DataTile
            label="Water Flow Cap"
            value={waterCapDisplay}
            data-testid="engine-water-cap-tile"
          />
        )}
        {/* B.6 — O-2 split fraction */}
        {hasSplit && (
          <DataTile
            label="Split (Op / ACB)"
            value={`${opPct}% / ${acbPct}%`}
            data-testid="engine-o2-split-tile"
          />
        )}
      </div>
      {miniChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          <span className="text-[10px] text-muted-foreground">Minimised:</span>
          {miniChips.map((chip, i) => (
            <span
              key={i}
              className="text-[10px] font-semibold leading-none px-2 pt-0.5 pb-1.5 rounded bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300"
            >
              {chip}
            </span>
          ))}
        </div>
      )}
      {/* B.6 — Shortfall amber chip */}
      {hasShortfall && (
        <div
          className="mt-2 flex items-start gap-1.5 rounded border border-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300"
          role="alert"
          data-testid="engine-shortfall-chip"
        >
          <span className="font-semibold shrink-0">Capacity short</span>
          <span>
            by {Math.round(shortfall_btuh).toLocaleString()} BTU/h
            {deliveredBtuh != null && target_cooling_btuh != null && (
              <> (delivered {deliveredBtuh.toLocaleString()} / target {Math.round(Number(target_cooling_btuh)).toLocaleString()})</>
            )}
          </span>
        </div>
      )}
      {strategy_note && (
        <div className="mt-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300" data-testid="engine-strategy-note">
          {strategy_note}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: Circuit Arrangement (O-2 only)
// ---------------------------------------------------------------------------

function CircuitArrangementSection({ piping }) {
  if (!piping) return null;
  const isSerial = piping === "serial";
  return (
    <Section title="Circuit Arrangement" data-testid="model-o-circuit-arrangement">
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`text-[10px] font-semibold uppercase tracking-wide leading-none px-2 pt-0.5 pb-1.5 rounded ${
            isSerial
              ? "bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-300"
              : "bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300"
          }`}
        >
          {isSerial ? "Serial" : "Parallel"}
        </span>
        <span className="text-xs text-muted-foreground">
          {isSerial
            ? "Water flows through Op coil first; ACB EWT = Op LWT (higher than chiller EWT)."
            : "Both Op and ACB coils receive the same chiller EWT simultaneously."}
        </span>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Shared coil air-side table (entering + leaving rows)
// ---------------------------------------------------------------------------

function CoilAirSideTable({ enteringDB, enteringW, enteringRH, leavingDB, leavingW, leavingRH, airflowCfm, isSI }) {
  return (
    <>
      <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle">
        <TableHeader>
          <TableRow>
            <TableHead>Air State</TableHead>
            <TableHead className="text-right">DB ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">RH (%)</TableHead>
            <TableHead className="text-right">{isSI ? "W (g/kg)" : "W (gr/lb)"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="font-medium">Entering</TableCell>
            <TableCell className="text-right tabular-nums">{fmtTemp(enteringDB, isSI)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtRH(enteringRH)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtGrains(enteringW, isSI)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="font-medium">Leaving (LAT)</TableCell>
            <TableCell className="text-right tabular-nums">{fmtTemp(leavingDB, isSI)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtRH(leavingRH)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtGrains(leavingW, isSI)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {airflowCfm != null && (
        <div className="mt-2 text-[10px] text-muted-foreground">
          Airflow:{" "}
          <span className="font-semibold tabular-nums">
            {fmtKind(airflowCfm, "flow", isSI)} {unitLabel("flow", isSI)}
          </span>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Section: Op Coil Performance (O-2 only)
// ---------------------------------------------------------------------------

function OpCoilSection({ speedRow, conditions, airSide, piping, isSI }) {
  if (!speedRow) return null;

  // Entering air: coil_entering_air_f from conditions (same for all speeds —
  // it's either room_dbt + 2°F or the user's air_on_temp override).
  // Room humidity ratio comes from air_side.room_w_grains.
  const enteringDB = conditions?.coil_entering_air_f;
  const enteringW = airSide?.room_w_grains; // W at room conditions
  const enteringRH = airSide?.room_rh_pct;

  // Leaving air: computed by backend in air_side (for O-2, op_leaving_db_f)
  const leavingDB = airSide?.op_leaving_db_f ?? airSide?.coil_leaving_db_f;
  const leavingW = airSide?.op_leaving_w_grains ?? airSide?.coil_leaving_w_grains;

  const isDry = speedRow.op_latent_btuh === 0 && speedRow.op_sensible_btuh > 0;

  return (
    <Section title="Op Coil Performance (Primary Air — Model Op)" data-testid="model-o-op-section">
      <div className="mb-2 flex gap-2 items-center">
        <WetDryBadge isDry={isDry} />
        {piping === "serial" && (
          <span className="text-[10px] text-muted-foreground">
            Serial first stage — Op LWT becomes ACB EWT
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Air side */}
        <div>
          <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">
            Air Side
          </div>
          <CoilAirSideTable
            enteringDB={enteringDB}
            enteringW={enteringW}
            enteringRH={enteringRH}
            leavingDB={leavingDB}
            leavingW={leavingW}
            leavingRH={airSide?.op_leaving_rh_pct ?? airSide?.coil_leaving_rh_pct ?? (isDry ? enteringRH : null)}
            airflowCfm={speedRow.primary_cfm}
            isSI={isSI}
          />
        </div>

        {/* Capacity + water */}
        <div>
          <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">
            Water-Side Capacity
          </div>
          <div className="text-[10px] text-muted-foreground mb-2">
            Heat removed from this airstream by the coil&apos;s water flow (not net room-side)
          </div>
          <div className="grid grid-cols-2 gap-2">
            <DataTile
              label="Coil-Side Sensible"
              value={`${fmtKind(speedRow.op_sensible_btuh, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
            />
            <DataTile
              label="Coil-Side Latent"
              value={
                speedRow.op_latent_btuh > 0
                  ? `${fmtKind(speedRow.op_latent_btuh, "capacity", isSI)} ${unitLabel("capacity", isSI)}`
                  : "— (dry coil)"
              }
            />
            <DataTile
              label="Coil-Side Total"
              value={`${fmtKind(speedRow.op_total_btuh, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
              accent="font-bold"
            />
            <DataTile
              label="Water EWT / LWT"
              value={`${fmtTemp(speedRow.op_ewt_f, isSI)} / ${fmtTemp(speedRow.op_lwt_f, isSI)}`}
              sub={`ΔT ${Number(speedRow.op_water_delta_t_f ?? 0).toFixed(1)} ${unitLabel("temp", isSI)}`}
            />
            <DataTile
              label="Water Flow"
              value={`${fmtKind(speedRow.op_water_flow_gpm, "waterFlow", isSI)} ${unitLabel("waterFlow", isSI)}`}
            />
            <DataTile
              label="Water-Side PD"
              value={`${fmtKind(speedRow.op_water_pd_fth2o, "pressure", isSI)} ${unitLabel("pressure", isSI)}`}
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: ACB Coil Performance (both O-1 and O-2)
// ---------------------------------------------------------------------------

function AcbCoilSection({ speedRow, conditions, airSide, isO2, piping, isSI }) {
  if (!speedRow) return null;

  // Entering air for ACB:
  //   O-1 / O-2 parallel: room air entering at ceiling level (coil_entering_air_f)
  //   O-2 serial: same ceiling-level temp (the secondary stream is room air in both cases)
  // Room humidity is carried at room conditions regardless of serial/parallel.
  const enteringDB = conditions?.coil_entering_air_f;
  const enteringW = airSide?.room_w_grains;
  const enteringRH = airSide?.room_rh_pct;

  // Leaving air: acb_leaving_db_f stored on air_side for O-2;
  // for O-1 use coil_leaving_db_f (single coil).
  const leavingDB = airSide?.acb_leaving_db_f ?? airSide?.coil_leaving_db_f;
  const leavingW = airSide?.acb_leaving_w_grains ?? airSide?.coil_leaving_w_grains;

  const isDry = speedRow.final_latent_btuh === 0 || (isO2 && speedRow.acb_latent_btuh === 0);
  const latentVal = isO2 ? speedRow.acb_latent_btuh : speedRow.final_latent_btuh;
  const sensVal = isO2 ? speedRow.acb_sensible_btuh : speedRow.final_sensible_btuh;
  const totalVal = isO2 ? speedRow.acb_total_btuh : speedRow.final_total_btuh;
  const ewt = isO2 ? speedRow.acb_ewt_f : conditions?.water_entering_f;
  const lwt = isO2 ? speedRow.acb_lwt_f : speedRow.leaving_water_f;
  const dt = isO2 ? speedRow.acb_water_delta_t_f : speedRow.water_delta_t_f;
  const gpm = isO2 ? speedRow.acb_water_flow_gpm : conditions?.water_flow_gpm;
  const pd = isO2 ? speedRow.acb_water_pd_fth2o : speedRow.water_pd_fth2o;

  const serialNote =
    isO2 && piping === "serial"
      ? "ACB EWT = Op LWT (serial — warmer than chiller EWT)"
      : isO2 && piping === "parallel"
      ? "ACB EWT = chiller EWT (parallel)"
      : null;

  return (
    <Section title="ACB Coil Performance (Secondary / Induced Air)" data-testid="model-o-acb-section">
      <div className="mb-2 flex gap-2 items-center flex-wrap">
        <WetDryBadge isDry={isDry} />
        {serialNote && (
          <span className="text-[10px] text-muted-foreground">{serialNote}</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Air side */}
        <div>
          <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">
            Air Side
          </div>
          <CoilAirSideTable
            enteringDB={enteringDB}
            enteringW={enteringW}
            enteringRH={enteringRH}
            leavingDB={leavingDB}
            leavingW={leavingW}
            leavingRH={airSide?.acb_leaving_rh_pct ?? airSide?.coil_leaving_rh_pct ?? (isDry ? enteringRH : null)}
            airflowCfm={speedRow.secondary_cfm}
            isSI={isSI}
          />
          <div className="mt-1 text-[10px] text-muted-foreground">
            Primary (nozzle) CFM:{" "}
            <span className="font-semibold tabular-nums">
              {fmtKind(speedRow.primary_cfm, "flow", isSI)} {unitLabel("flow", isSI)}
            </span>
            {" "}— Total supply:{" "}
            <span className="font-semibold tabular-nums">
              {fmtKind(speedRow.supply_cfm, "flow", isSI)} {unitLabel("flow", isSI)}
            </span>
          </div>
        </div>

        {/* Capacity + water */}
        <div>
          <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">
            Water-Side Capacity
          </div>
          <div className="text-[10px] text-muted-foreground mb-2">
            Heat removed from this airstream by the coil&apos;s water flow (not net room-side)
          </div>
          <div className="grid grid-cols-2 gap-2">
            <DataTile
              label="Coil-Side Sensible"
              value={`${fmtKind(sensVal, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
            />
            <DataTile
              label="Coil-Side Latent"
              value={
                latentVal > 0
                  ? `${fmtKind(latentVal, "capacity", isSI)} ${unitLabel("capacity", isSI)}`
                  : "— (dry coil)"
              }
            />
            <DataTile
              label="Coil-Side Total"
              value={`${fmtKind(totalVal, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
              accent="font-bold"
            />
            <DataTile
              label="Water EWT / LWT"
              value={`${fmtTemp(ewt, isSI)} / ${fmtTemp(lwt, isSI)}`}
              sub={dt != null ? `ΔT ${Number(dt).toFixed(1)} ${unitLabel("temp", isSI)}` : null}
            />
            <DataTile
              label="Water Flow"
              value={`${fmtKind(gpm, "waterFlow", isSI)} ${unitLabel("waterFlow", isSI)}`}
            />
            <DataTile
              label="Water-Side PD"
              value={`${fmtKind(pd, "pressure", isSI)} ${unitLabel("pressure", isSI)}`}
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: System Totals
// ---------------------------------------------------------------------------

function SystemTotalsSection({ speedRow, airSide, isO2, piping, isSI }) {
  if (!speedRow) return null;

  const totalCfm = speedRow.supply_cfm ?? speedRow.total_supply_cfm;
  const totalCooling = airSide?.air_total_btuh ?? speedRow.final_total_btuh;
  const totalSens = airSide?.air_sensible_btuh ?? speedRow.final_sensible_btuh;
  const totalLat = airSide?.air_latent_btuh ?? speedRow.final_latent_btuh;

  // Water-side totals: for O-2 these are pre-computed on the speed row.
  // For O-1 there's only one coil, so it's just the single-coil water side.
  const chillerFlow = isO2
    ? speedRow.total_water_flow_gpm
    : speedRow.acb_water_flow_gpm ?? speedRow.water_flow_gpm;
  const chillerPd = isO2
    ? speedRow.total_water_pd_fth2o
    : speedRow.acb_water_pd_fth2o ?? speedRow.water_pd_fth2o;
  const waterSideTotalBtuh = isO2
    ? speedRow.final_total_btuh
    : speedRow.final_total_btuh;

  const waterNote =
    isO2 && piping === "serial"
      ? "Serial: one water loop through both coils in sequence."
      : isO2 && piping === "parallel"
      ? "Parallel: separate water circuits; chiller flow = Op + ACB combined."
      : null;

  return (
    <Section title="System Totals — Chiller Plant Sizing">
      {/* Water-side plant load */}
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
        Water-Side (Chiller Plant Load)
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
        <DataTile
          label="Total Supply Air to Room"
          value={`${fmtKind(totalCfm, "flow", isSI)} ${unitLabel("flow", isSI)}`}
          sub={
            isO2
              ? `Op ${fmtKind(speedRow.primary_cfm, "flow", isSI)} + ACB ${fmtKind(speedRow.secondary_cfm, "flow", isSI)} ${unitLabel("flow", isSI)}`
              : `Primary ${fmtKind(speedRow.primary_cfm, "flow", isSI)} + Secondary ${fmtKind(speedRow.secondary_cfm, "flow", isSI)} ${unitLabel("flow", isSI)}`
          }
        />
        <DataTile
          label="Total Chiller Flow"
          value={`${fmtKind(chillerFlow, "waterFlow", isSI)} ${unitLabel("waterFlow", isSI)}`}
          accent="font-bold text-blue-700 dark:text-blue-400"
          sub={waterNote}
        />
        <DataTile
          label="Water-Side Total Capacity"
          value={`${fmtKind(waterSideTotalBtuh, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
          sub="Sum of all coil water-side capacities (chiller plant must deliver)"
        />
      </div>
      {/* Divider */}
      <div className="border-t border-border my-3" />
      {/* Air-side room-delivered */}
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
        Air-Side (Net Room-Delivered Capacity)
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <DataTile
          label="Net Delivered to Room (Air-Side Total)"
          value={`${fmtKind(totalCooling, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
          accent="font-bold"
          sub="SA enthalpy difference × total supply CFM"
        />
        <DataTile
          label="Room Sensible, delivered (air-side)"
          value={`${fmtKind(totalSens, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
        />
        <DataTile
          label="Room Latent, delivered (air-side)"
          value={`${fmtKind(totalLat, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
        />
        {isO2 && (
          <DataTile
            label="Chiller Water-Side PD"
            value={`${fmtKind(chillerPd, "pressure", isSI)} ${unitLabel("pressure", isSI)}`}
            sub={piping === "serial" ? "Series PD (Op + ACB additive)" : "Parallel PD (higher of Op, ACB)"}
          />
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: Psychrometric Points Table
// ---------------------------------------------------------------------------

function PsychRow({ label, dbF, wGrains, rhPct, isSI }) {
  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className="text-right tabular-nums">{fmtTemp(dbF, isSI)}</TableCell>
      <TableCell className="text-right tabular-nums">{fmtRH(rhPct)}</TableCell>
      <TableCell className="text-right tabular-nums">{fmtGrains(wGrains, isSI)}</TableCell>
    </TableRow>
  );
}

function PsychrometricPointsSection({ speedRow, conditions, airSide, isO2, piping, isSI }) {
  if (!airSide) return null;

  const roomDB = airSide.room_db_f;
  const roomRH = airSide.room_rh_pct;
  const roomW = airSide.room_w_grains;
  const enteringDB = conditions?.coil_entering_air_f;
  const supplyDB = airSide.supply_db_f;
  const supplyW = airSide.supply_w_grains;
  const supplyRH = airSide.supply_rh_pct;

  // O-2 has separate leaving states for each coil
  const opLeavingDB = airSide.op_leaving_db_f;
  const opLeavingW = airSide.op_leaving_w_grains;
  const acbLeavingDB = airSide.acb_leaving_db_f;
  const acbLeavingW = airSide.acb_leaving_w_grains;
  // O-1 has a single coil_leaving
  const coilLeavingDB = airSide.coil_leaving_db_f;
  const coilLeavingW = airSide.coil_leaving_w_grains;

  return (
    <Section title="Psychrometric State Points (Tabular)">
      <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle">
        <TableHeader>
          <TableRow>
            <TableHead>State Point</TableHead>
            <TableHead className="text-right">DB ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">RH (%)</TableHead>
            <TableHead className="text-right">{isSI ? "W (g/kg)" : "W (gr/lb)"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <PsychRow label="RA — Room Air" dbF={roomDB} wGrains={roomW} rhPct={roomRH} isSI={isSI} />
          {enteringDB != null && (
            <PsychRow
              label="AI — Air entering coils (ceiling plenum)"
              dbF={enteringDB}
              wGrains={roomW}
              rhPct={roomRH}
              isSI={isSI}
            />
          )}
          {isO2 && opLeavingDB != null && (
            <PsychRow
              label="OP-AO — Op Coil leaving air (LAT)"
              dbF={opLeavingDB}
              wGrains={opLeavingW}
              rhPct={null}
              isSI={isSI}
            />
          )}
          {isO2 && acbLeavingDB != null && (
            <PsychRow
              label="ACB-AO — ACB Coil leaving air (LAT)"
              dbF={acbLeavingDB}
              wGrains={acbLeavingW}
              rhPct={null}
              isSI={isSI}
            />
          )}
          {!isO2 && coilLeavingDB != null && (
            <PsychRow
              label="AO — ACB Coil leaving air (LAT)"
              dbF={coilLeavingDB}
              wGrains={coilLeavingW}
              rhPct={null}
              isSI={isSI}
            />
          )}
          <PsychRow
            label="SA — Mixed supply air to room"
            dbF={supplyDB}
            wGrains={supplyW}
            rhPct={supplyRH}
            isSI={isSI}
          />
        </TableBody>
      </Table>
      <p className="text-[10px] text-muted-foreground mt-1">
        State points at the cascade-selected operating speed. RH shown as "—" for
        post-coil leaving states (condensate removed; RH not computed in model).
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: Water-Side Summary
// ---------------------------------------------------------------------------

// modulatedWater shape: { flow, lwt, dt, pd } — from optimal_point when available.
// For O-2: { opFlow, opLwt, opDt, opPd, acbFlow, acbLwt, acbDt, acbPd }
function WaterSideSummarySection({ speedRow, conditions, isO2, piping, modulatedWater, isSI }) {
  if (!speedRow) return null;

  const ewt = conditions?.water_entering_f;
  const mw = modulatedWater || {};

  const rows = isO2
    ? [
        {
          label: "Op Coil (primary air)",
          ewt: speedRow.op_ewt_f ?? ewt,
          lwt: mw.opLwt ?? speedRow.op_lwt_f,
          gpm: mw.opFlow ?? speedRow.op_water_flow_gpm,
          pd: mw.opPd ?? speedRow.op_water_pd_fth2o,
          cap: speedRow.op_total_btuh,
        },
        {
          label: `ACB Coil (secondary air)${piping === "serial" ? " — water in series" : ""}`,
          ewt: speedRow.acb_ewt_f,
          lwt: mw.acbLwt ?? speedRow.acb_lwt_f,
          gpm: mw.acbFlow ?? speedRow.acb_water_flow_gpm,
          pd: mw.acbPd ?? speedRow.acb_water_pd_fth2o,
          cap: speedRow.acb_total_btuh,
        },
      ]
    : [
        {
          label: "ACB Coil",
          ewt: ewt,
          lwt: mw.lwt ?? speedRow.leaving_water_f,
          gpm: mw.flow ?? conditions?.water_flow_gpm,
          pd: mw.pd ?? speedRow.water_pd_fth2o,
          cap: speedRow.final_total_btuh,
        },
      ];

  const totalGpm = isO2
    ? speedRow.total_water_flow_gpm
    : (mw.flow ?? conditions?.water_flow_gpm);
  const totalPd = isO2
    ? speedRow.total_water_pd_fth2o
    : (mw.pd ?? speedRow.water_pd_fth2o);
  const totalCap = speedRow.final_total_btuh;

  const pdNote =
    isO2 && piping === "serial"
      ? "Serial: PD is additive (Op + ACB in series)"
      : isO2 && piping === "parallel"
      ? "Parallel: PD = higher of Op / ACB"
      : null;

  return (
    <Section title="Water-Side Summary (Executive — Plant Sizing)">
      <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle">
        <TableHeader>
          <TableRow>
            <TableHead>Coil</TableHead>
            <TableHead className="text-right">Flow ({unitLabel("waterFlow", isSI)})</TableHead>
            <TableHead className="text-right">EWT ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">LWT ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">PD ({unitLabel("pressure", isSI)})</TableHead>
            <TableHead className="text-right">Capacity ({unitLabel("capacity", isSI)})</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.label}>
              <TableCell className="font-medium">{r.label}</TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtKind(r.gpm, "waterFlow", isSI)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtKind(r.ewt, "temp", isSI)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtKind(r.lwt, "temp", isSI)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtKind(r.pd, "pressure", isSI)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtKind(r.cap, "capacity", isSI)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-muted font-semibold">
            <TableCell>Total (chiller plant)</TableCell>
            <TableCell className="text-right tabular-nums">
              {fmtKind(totalGpm, "waterFlow", isSI)}
            </TableCell>
            <TableCell>{/* EWT blank on total row */}</TableCell>
            <TableCell>{/* LWT blank on total row */}</TableCell>
            <TableCell className="text-right tabular-nums">
              {fmtKind(totalPd, "pressure", isSI)}
              {pdNote && (
                <div className="text-[9px] font-normal text-muted-foreground leading-tight">
                  {pdNote}
                </div>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {fmtKind(totalCap, "capacity", isSI)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Main export: ModelODetailView
// ---------------------------------------------------------------------------

/**
 * Renders the full comprehensive detail view for a Model O-1 or O-2 room result.
 *
 * Props:
 *   raw       — backend API response (raw.results[0].cooling for multi-length shape,
 *               or the flat calculate_o1/calculate_o2 response for single)
 *   isSI      — boolean; true = SI display units
 *   showChart — boolean; whether to render the psychrometric chart
 *   isO2      — boolean; true = Model O-2 (dual coil), false = Model O-1
 */
export default function ModelODetailView({ raw, isSI, showChart = true, isO2 = false, project = null, room = null }) {
  if (!raw) return null;

  // Multi-length endpoint wraps results: raw.results[0].cooling
  // The O-1/O-2 calculators are always called via the multi-length endpoint,
  // so this shape is canonical. Fall back to raw directly for unit tests.
  const cooling = raw?.results?.[0]?.cooling ?? raw;
  if (!cooling) return null;

  const conditions = cooling.conditions ?? {};
  const piping = cooling.piping ?? null; // "serial" | "parallel" | null (O-1)
  const speeds = cooling.speeds ?? [];

  // Resolve cascade speed using the shared helper — honours optimal_point.operating_speed
  // before falling back to project.advanced.acb_fan_speed → "med" → first speed.
  // `project` is not available here (ModelODetailView doesn't receive it), so we pass
  // null and rely on optimal_point alone. The project-level fallback only matters for
  // max-flow sweeps (no target) where optimal_point is absent; in that case the
  // existing "med" fallback is correct and unchanged.
  const { speedRow: cascadeSpeed, opt: cascadeOpt, speed: cascadeSpeedKey } =
    pickCascadeOperatingPoint(raw?.results?.[0] || raw, null);

  const airSide = cascadeSpeed?.air_side ?? null;
  const psych = cooling.psych_chart ?? null;

  // Build modulated water props from optimal_point for WaterSideSummarySection.
  // optimal_point carries the post-cascade actual water flow / LWT; prefer those
  // over the speed-row values which reflect the unmodulated ceiling.
  const modulatedWater = isO2
    ? {
        opFlow: cascadeOpt.op_water_flow_gpm ?? null,
        opLwt: cascadeOpt.op_lwt_f ?? null,
        opDt: cascadeOpt.op_water_delta_t_f ?? null,
        opPd: cascadeOpt.op_water_pd_fth2o ?? null,
        acbFlow: cascadeOpt.acb_water_flow_gpm ?? null,
        acbLwt: cascadeOpt.acb_lwt_f ?? null,
        acbDt: cascadeOpt.acb_water_delta_t_f ?? null,
        acbPd: cascadeOpt.acb_water_pd_fth2o ?? null,
      }
    : {
        flow: cascadeOpt.water_flow_gpm ?? null,
        lwt: cascadeOpt.leaving_water_f ?? null,
        dt: cascadeOpt.water_delta_t_f ?? null,
        pd: cascadeOpt.water_pd_fth2o ?? null,
      };

  return (
    <div data-testid="model-o-detail-view">
      {/* Engine Selection Summary — shown when optimal_point is present */}
      <EngineSelectionSummary
        optimalPoint={cascadeOpt && Object.keys(cascadeOpt).length > 0 ? cascadeOpt : null}
        isFACBFamily={true}
        isSI={isSI}
        noCoilPipes={project?.advanced?.no_coil_pipes ?? 2}
        selectedCircuits={raw?.results?.[0]?.circuits ?? null}
      />

      {/* Circuit arrangement — O-2 only */}
      {isO2 && piping && (
        <CircuitArrangementSection piping={piping} />
      )}

      {/* Op Coil — O-2 only */}
      {isO2 && cascadeSpeed && (
        <OpCoilSection
          speedRow={cascadeSpeed}
          conditions={conditions}
          airSide={airSide}
          piping={piping}
          isSI={isSI}
        />
      )}

      {/* ACB Coil — always */}
      {cascadeSpeed && (
        <AcbCoilSection
          speedRow={cascadeSpeed}
          conditions={conditions}
          airSide={airSide}
          isO2={isO2}
          piping={piping}
          isSI={isSI}
        />
      )}

      {/* Water-side summary */}
      {cascadeSpeed && (
        <WaterSideSummarySection
          speedRow={cascadeSpeed}
          conditions={conditions}
          isO2={isO2}
          piping={piping}
          modulatedWater={modulatedWater}
          isSI={isSI}
        />
      )}

      {/* Per-speed summary table (Lo / Med / Hi) */}
      {speeds.length > 0 && (
        <Section title="Cooling — All Fan Speeds">
          <p className="text-[10px] text-muted-foreground mb-2">
            Op Sens (coil) and ACB Sens (coil) are water-side coil capacities. Room Sens / Room Lat / Room Total are net air-side values delivered to the space.
          </p>
          <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle">
            <TableHeader>
              <TableRow>
                <TableHead>Speed</TableHead>
                <TableHead className="text-right">Supply CFM ({unitLabel("flow", isSI)})</TableHead>
                {isO2 && <TableHead className="text-right">Op Sens, coil ({unitLabel("capacity", isSI)})</TableHead>}
                {isO2 && <TableHead className="text-right">ACB Sens, coil ({unitLabel("capacity", isSI)})</TableHead>}
                <TableHead className="text-right">Room Sens, delivered ({unitLabel("capacity", isSI)})</TableHead>
                <TableHead className="text-right">Room Lat, delivered ({unitLabel("capacity", isSI)})</TableHead>
                <TableHead className="text-right font-bold">Room Total, delivered ({unitLabel("capacity", isSI)})</TableHead>
                <TableHead className="text-right">LWT ({unitLabel("temp", isSI)})</TableHead>
                <TableHead className="text-right">ΔT ({unitLabel("temp", isSI)})</TableHead>
                <TableHead className="text-right">Power (W)</TableHead>
                <TableHead className="text-right">NC</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {speeds.map((s) => {
                const ai = s.air_side ?? {};
                const totalSens = ai.air_sensible_btuh ?? s.final_sensible_btuh;
                const totalLat = ai.air_latent_btuh ?? s.final_latent_btuh;
                const totalCap = ai.air_total_btuh ?? s.final_total_btuh;
                const isCascadeRow = s.speed === cascadeSpeedKey;
                return (
                  <TableRow key={s.speed} className={isCascadeRow ? "bg-muted/40" : ""}>
                    <TableCell className="capitalize font-medium">
                      {s.speed}{isCascadeRow && " (Optimized)"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtKind(s.supply_cfm, "flow", isSI)}
                    </TableCell>
                    {isO2 && (
                      <TableCell className="text-right tabular-nums">
                        {fmtKind(s.op_sensible_btuh, "capacity", isSI)}
                      </TableCell>
                    )}
                    {isO2 && (
                      <TableCell className="text-right tabular-nums">
                        {fmtKind(s.acb_sensible_btuh, "capacity", isSI)}
                      </TableCell>
                    )}
                    <TableCell className="text-right tabular-nums">
                      {fmtKind(totalSens, "capacity", isSI)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtKind(totalLat, "capacity", isSI)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-bold">
                      {fmtKind(totalCap, "capacity", isSI)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtKind(s.leaving_water_f, "temp", isSI)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.water_delta_t_f != null ? Number(s.water_delta_t_f).toFixed(1) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.fan_watts != null ? Math.round(s.fan_watts) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.nc_level != null ? Math.round(s.nc_level) : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="mt-2 text-[10px] text-muted-foreground italic">
            Independently Measured NC Values - Pending Certification
          </p>
        </Section>
      )}

      {/* System Totals */}
      {cascadeSpeed && (
        <SystemTotalsSection
          speedRow={cascadeSpeed}
          airSide={airSide}
          isO2={isO2}
          piping={piping}
          isSI={isSI}
        />
      )}

      {/* Psychrometric chart */}
      {showChart && psych?.points && (
        <Section title="Psychrometric Chart">
          <PsychrometricChart data={psych} isSI={isSI} />
        </Section>
      )}

      {/* Psychrometric points table */}
      {airSide && (
        <PsychrometricPointsSection
          speedRow={cascadeSpeed}
          conditions={conditions}
          airSide={airSide}
          isO2={isO2}
          piping={piping}
          isSI={isSI}
        />
      )}

      {/* Sizing footnote — shown when project context is available */}
      {project && (
        <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed italic" data-testid="model-o-sizing-footnote">
          {formatSizingFootnote(project, room ? [room] : null)}
        </p>
      )}

    </div>
  );
}
